// src/components/agenda/AgendaActionMenu.js
import React, { useState, useEffect, useRef } from 'react';

/**
 * Das "Aktions-Menü" für einen Agendapunkt (jetzt mit Plus-Icon).
 * Zeigt jetzt Aktionen basierend auf der Rolle des Benutzers an (Moderator vs. Teilnehmer).
 */
const AgendaActionMenu = ({ 
  onAddTask, 
  onAddSubPoint, 
  onDeleteItem, 
  onAddPoll, 
  onManagePermissions, // <-- Diese Funktion öffnet jetzt den Dialog zum Hinzufügen von Gästen
  itemId, 
  isTask,
  isModerator, 
  canEdit,     
  onProposeItem  
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target) &&
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuRef]);

  const handleToggleMenu = (e) => {
    e.stopPropagation(); 
    setIsOpen(prev => !prev);
  };

  const handleActionClick = (actionFn) => {
    if (actionFn) actionFn();
    setIsOpen(false); 
  };

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={handleToggleMenu}
        className="w-8 h-8 flex items-center justify-center bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full hover:bg-gray-300 dark:hover:bg-gray-600 transition-all duration-200 ease-in-out transform hover:scale-110 text-sm font-medium shadow-sm"
        title="Aktionen hinzufügen"
      >
        <span className="material-symbols-outlined text-xl">add</span>
      </button>

      {isOpen && (
        <div
          ref={menuRef}
          className="absolute right-0 mt-2 w-72 bg-white dark:bg-gray-700 rounded-lg shadow-xl z-10 border border-gray-200 dark:border-gray-600 overflow-hidden"
        >
          {/* === MODERATOR/ERSTELLER-AKTIONEN === */}
          {canEdit && (
            <>
              <button
                onClick={() => handleActionClick(onAddTask)}
                className="flex items-center w-full px-4 py-3 text-left text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors duration-200"
              >
                <span className="material-symbols-outlined text-xl mr-3">add_task</span>
                Aufgabe hinzufügen
              </button>
              
              <button
                onClick={() => handleActionClick(() => onAddSubPoint(itemId))}
                className="flex items-center w-full px-4 py-3 text-left text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors duration-200"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 3h3m-6 3h6" />
                </svg>
                Unterpunkt hinzufügen
              </button>

              <button
                onClick={() => handleActionClick(onAddPoll)}
                className="flex items-center w-full px-4 py-3 text-left text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors duration-200"
              >
                <span className="material-symbols-outlined text-xl mr-3">how_to_vote</span>
                Umfrage erstellen
              </button>

              {/* === NEU: Gast hinzufügen statt Sichtbarkeit === */}
              <button
                onClick={() => handleActionClick(onManagePermissions)}
                className="flex items-center w-full px-4 py-3 text-left text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors duration-200 border-t border-gray-100 dark:border-gray-600"
              >
                <span className="material-symbols-outlined text-xl mr-3">person_add_alt</span>
                <div>
                    <span className="block font-medium leading-none mb-1">Gast hinzufügen</span>
                    <span className="block text-xs text-gray-500 dark:text-gray-400">Nur Zugriff auf diesen Punkt</span>
                </div>
              </button>

              <button
                onClick={() => handleActionClick(() => onDeleteItem(itemId, isTask))}
                className="flex items-center w-full px-4 py-3 text-left text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-800 transition-colors duration-200 border-t border-gray-100 dark:border-gray-600"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Löschen
              </button>
            </>
          )}

          {/* === TEILNEHMER-AKTION === */}
          {!isModerator && (
            <button
              onClick={() => handleActionClick(onProposeItem)}
              className="flex items-center w-full px-4 py-3 text-left text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-800 transition-colors duration-200"
            >
              <span className="material-symbols-outlined text-xl mr-3">add_comment</span>
              Unterpunkt vorschlagen
            </button>
          )}

        </div>
      )}
    </div>
  );
};

export default AgendaActionMenu;